import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Row, Col, Button, Card, Modal, Form, Tab, Nav, Accordion } from 'react-bootstrap';
import JoditEditor from 'jodit-react';

const CHANNEL_OPTIONS = [
    { value: 'EMAIL', label: 'Email' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
];

const ACTION_OPTIONS = [
    { value: 'OVERVIEW', label: 'Overview' },
    { value: 'MEETING', label: 'Meeting' },
];

const ACTION_DESCRIPTIONS = {
    overview: 'Sent when the user requests more information or an overview.',
    meeting: 'Sent when a calendar meeting is successfully booked.',
};

function VCTemplates(props) {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [updatingdata, setUpdatingdata] = useState(false);
    const [modifymode, setModifymode] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        action: 'OVERVIEW',
        channel: 'EMAIL',
        subject: '',
        message: '',
    });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [validated, setValidated] = useState(false);
    const [saving, setSaving] = useState(false);

    const fileRef = useRef();
    const editorRef = useRef(null);

    const editorConfig = useMemo(() => ({
        readonly: false,
        placeholder: 'Start typing your message...',
        height: 220,
        toolbarButtonSize: 'small',
        buttons: 'bold,italic,underline,strikethrough,|,ul,ol,|,font,fontsize,paragraph,|,link,|,align,|,hr,br,|,source',
        showCharsCounter: false,
        showXPathInStatusbar: false,
        statusbar: false,
        showPlaceholder:false
    }), []);

    const getTemplates = () => {
        axios.get(`/vcactiontemplates/${props.uid}`)
            .then(res => setTemplates(res.data))
            .catch(err => console.log(err));
    };

    useEffect(() => {
        getTemplates();
    }, [updatingdata]);

    const resetForm = () => {
        setFormData({ name: '', action: 'OVERVIEW', channel: 'EMAIL', subject: '', message: '' });
        setSelectedFiles([]);
        setValidated(false);
        setModifymode(false);
        setSelectedTemplate({});
        if (fileRef.current) fileRef.current.value = '';
    };

    const openCreate = () => {
        resetForm();
        setShowForm(true);
    };

    const openModify = (template) => {
        setSelectedTemplate(template);
        setModifymode(true);
        axios.get(`/vcactiontemplate/${template.id}`)
            .then(res => {
                // API returns a single object now
                const t = res.data;
                setFormData({
                    id: t.id,
                    name: t.name || '',
                    action: t.action || 'OVERVIEW',
                    channel: t.channel || 'EMAIL',
                    subject: t.subject || '',
                    message: t.message || '',
                    attachments: t.attachments,
                });
                setSelectedFiles([]);
            })
            .catch(err => console.log(err));
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        resetForm();
        getTemplates();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFilesChange = (e) => {
        setSelectedFiles(Array.from(e.target.files));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.checkValidity()) { setValidated(true); return; }

        setSaving(true);
        try {
            const data = new FormData();
            data.set('name', formData.name);
            data.set('action', formData.action);
            data.set('channel', formData.channel);
            data.set('subject', formData.subject || '');
            data.set('message', formData.message || '');
            data.set('userid', props.uid);
            if (fileRef.current && fileRef.current.files)
                for (const file of Array.from(fileRef.current.files)) data.append('files', file);

            const response = await fetch('/api/vcactiontemplate', {
                method: 'POST',
                body: data,
                headers: { Authorization: `Bearer ${sessionStorage.getItem('jwttoken')}` },
            });
            if (response.ok) {
                if (formData.channel === 'WHATSAPP') {
                    alert('Template created and submitted for Meta approval. You will be able to use it once it is approved.');
                } else {
                    alert('Template created successfully.');
                }
                closeForm();
            }
            else { const err = await response.json().catch(() => ({})); alert(err.error || 'Something went wrong. Please try again.'); }
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!event.currentTarget.checkValidity()) { setValidated(true); return; }

        setSaving(true);
        try {
            const data = new FormData();
            data.set('id', formData.id);
            data.set('name', formData.name);
            data.set('action', formData.action);
            data.set('channel', formData.channel);
            data.set('subject', formData.subject || '');
            data.set('message', formData.message || '');
            data.set('userid', props.uid);
            if (fileRef.current && fileRef.current.files)
                for (const file of Array.from(fileRef.current.files)) data.append('files', file);

            const response = await fetch('/api/vcactiontemplate', {
                method: 'PUT',
                body: data,
                headers: { Authorization: `Bearer ${sessionStorage.getItem('jwttoken')}` },
            });
            if (response.ok) {
                const result = await response.json().catch(() => ({}));
                if (formData.channel === 'WHATSAPP') {
                    if (result.whatsapp_error) {
                        alert(`Template saved, but Meta rejected the submission:\n\n${result.whatsapp_error}\n\nPlease fix the message and save again.`);
                    } else {
                        alert('Template updated and submitted for Meta approval. You will be able to use it once it is approved.');
                    }
                } else {
                    alert('Template updated successfully.');
                }
                closeForm();
            }
            else { const err = await response.json().catch(() => ({})); alert(err.error || 'Something went wrong. Please try again.'); }
        } finally {
            setSaving(false);
        }
    };

    const deleteTemplate = (template) => {
        setSelectedTemplate(template);
        if (window.confirm('Are you sure you want to delete this template?')) {
            setUpdatingdata(prev => !prev);
            axios.delete(`/vcactiontemplate/${template.id}`)
                .then(() => setUpdatingdata(prev => !prev))
                .catch(err => console.log(err));
        }
    };

    const actionLabel = (action) => ACTION_OPTIONS.find(a => a.value === action)?.label || action;
    const channelLabel = (channel) => CHANNEL_OPTIONS.find(c => c.value === channel)?.label || channel;

    return (
        <React.Fragment>
            <div className="w-100 h-100 d-flex flex-column overflow-hidden">
                <Tab.Container defaultActiveKey="vctemplates" activeKey="vctemplates">
                    <Nav variant="tabs" className="fs-6 border">
                        <Nav.Item>
                            <Nav.Link eventKey="vctemplates">Message Templates</Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="ms-auto">
                            <Button title="New Action Template" className="btn btn-primary mx-1 float-right" onClick={openCreate}>
                                <i className="bi bi-plus text-white" style={{ fontWeight: 'bold' }}></i>
                            </Button>
                            <Button title="Refresh" className="btn btn-primary mx-1 float-right" onClick={getTemplates}>
                                <i className="bi bi-arrow-repeat"></i>
                            </Button>
                        </Nav.Item>
                    </Nav>

                    <Tab.Content className="w-100" style={{ height: 'calc(100% - 34px)' }} animation={true} mountOnEnter={true} unmountOnExit={true}>
                        <Tab.Pane className="w-100 h-100" eventKey="vctemplates">
                            <div className="m-2" style={{ maxHeight: '99%', display: 'grid', overflowX: 'hidden', overflowY: 'auto', gap: '4px', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
                                {templates?.length === 0 && (
                                    <div className="text-muted text-center mt-4 py-4" style={{ gridColumn: '1 / -1' }}>
                                        No action templates yet. 
                                    </div>
                                )}
                                {templates?.length > 0 && templates?.map((row, index) => (
                                    <Col key={row.id} xs={1} className="m-1" style={{ width: '100%', boxSizing: 'border-box' }}>
                                        <Card
                                            onClick={() => setSelectedTemplate(templates[index])}
                                            title={`Template [ID-${row.id}]`}
                                            className={`text-primary ${row.id === selectedTemplate.id ? 'border-primary shadow-lg' : ''}`}
                                        >
                                            <Card.Header className="text-white bg-primary p-0 align-items-center">
                                                <div className="d-flex flex-row">
                                                    <div className="p-2">
                                                        <span>{`Template [ID-${row.id}]`}</span>
                                                    </div>
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-2 d-flex align-content-end flex-column">
                                                <Card.Text>
                                                    {/* Name */}
                                                    <Row className="mx-0 border-top"><Col className="p-0"><b>Name</b></Col></Row>
                                                    <Row className="mx-0"><Col className="ps-1">{row.name || '—'}</Col></Row>

                                                    {/* Channel */}
                                                    <Row className="mx-0 border-top"><Col className="p-0"><b>Channel</b></Col></Row>
                                                    <Row className="mx-0"><Col className="ps-1">{channelLabel(row.channel)}</Col></Row>

                                                    {/* Action */}
                                                    <Row className="mx-0 border-top"><Col className="p-0"><b>Action</b></Col></Row>
                                                    <Row className="mx-0 border-bottom">
                                                        <Col className="ps-1">
                                                            <span className={`badge ${row.action === 'MEETING' ? 'bg-success' : 'bg-info'} text-white`} style={{ fontSize: '0.75rem' }}>
                                                                {actionLabel(row.action)}
                                                            </span>
                                                        </Col>
                                                    </Row>

                                                    {/* Subject preview */}
                                                    {row.subject && (
                                                        <>
                                                            <Row className="mx-0 border-top"><Col className="p-0"><b>Subject</b></Col></Row>
                                                            <Row className="mx-0">
                                                                <Col className="ps-1" style={{ fontSize: '0.85rem', color: '#555' }}>
                                                                    {row.subject.length > 40 ? row.subject.substring(0, 37) + '...' : row.subject}
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}

                                                    {/* Meta Status (WhatsApp only) */}
                                                    {row.channel === 'WHATSAPP' && (
                                                        <>
                                                            <Row className="mx-0 border-top"><Col className="p-0"><b>Status</b></Col></Row>
                                                            <Row className="mx-0">
                                                                <Col className="ps-1">
                                                                    {String(row.status) === '0' && <span className="badge bg-warning text-dark">Pending</span>}
                                                                    {String(row.status) === '1' && <span className="badge bg-success text-white">Approved</span>}
                                                                    {String(row.status) === '2' && <span className="badge bg-danger text-white">Rejected</span>}
                                                                    {String(row.status) === '3' && <span className="badge bg-danger text-white">Failed</span>}
                                                                    {(row.status === null || row.status === undefined) && <span className="badge bg-secondary text-white">Unsubmitted</span>}
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}
                                                </Card.Text>

                                                <Card.Text className="m-0 p-0">
                                                    <Row className="w-100 m-0">
                                                        <Col className="p-0 d-flex">
                                                            <Button
                                                                id={`btnm-${row.id}`}
                                                                size="sm"
                                                                variant="outline-primary"
                                                                style={{ borderRadius: '50%' }}
                                                                className="me-1 border border-gray"
                                                                title={row.channel === 'WHATSAPP' && (String(row.status) === '0' || String(row.status) === '1') ? `WhatsApp template is ${String(row.status) === '1' ? 'approved' : 'pending review'} and cannot be edited. Only rejected templates can be edited.` : 'Edit Template'}
                                                                disabled={row.channel === 'WHATSAPP' && (String(row.status) === '0' || String(row.status) === '1')}
                                                                onClick={(e) => { e.stopPropagation(); openModify(templates[index]); }}
                                                            >
                                                                <i className="bi bi-pencil-square"></i>
                                                            </Button>
                                                            <Button
                                                                id={`btnd-${row.id}`}
                                                                size="sm"
                                                                variant="outline-primary"
                                                                title="Delete Template"
                                                                style={{ borderRadius: '50%' }}
                                                                className="ms-auto border border-gray"
                                                                onClick={(e) => { e.stopPropagation(); deleteTemplate(templates[index]); }}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Card.Text>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </div>
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </div>

            {/* Create / Modify Modal */}
            <Modal show={showForm} onHide={closeForm} centered size="lg" backdrop="static" enforceFocus={false}>
                <Modal.Header closeButton className="py-2 bg-primary text-white">
                    <div className="fs-5">{modifymode ? 'Modify Action Template' : 'Create Action Template'}</div>
                </Modal.Header>
                <Modal.Body className="m-1 p-3">
                    <Form noValidate validated={validated} onSubmit={modifymode ? handleUpdate : handleSubmit}>

                        {/* Template Name */}
                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Template Name <span style={{ color: 'red' }}>*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        placeholder="e.g., Product Overview Email"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">Please provide a template name.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Channel + Action */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Channel</Form.Label>
                                    <Form.Control as="select" name="channel" value={formData.channel} onChange={handleChange}>
                                        {CHANNEL_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Type</Form.Label>
                                    <Form.Control as="select" name="action" value={formData.action} onChange={handleChange}>
                                        {ACTION_OPTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                    </Form.Control>
                                    {formData.action && (
                                        <Form.Text className="text-muted">{ACTION_DESCRIPTIONS[formData.action]}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Accordion for template content */}
                        <Accordion defaultActiveKey="content" className="mb-3">
                            <Accordion.Item eventKey="content">
                                <Accordion.Header>
                                    <span className="fw-semibold">
                                        {formData.action === 'MEETING' ? 'Meeting Confirmation Template' : 'Overview Template'}
                                    </span>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                                        {ACTION_DESCRIPTIONS[formData.action] || 'Configure the message content for this action.'}
                                    </p>

                                    {/* Subject */}
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group>
                                                <Form.Label className="fw-bold">Subject</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="subject"
                                                    placeholder={formData.action === 'MEETING' ? 'e.g., Meeting Confirmed!' : 'e.g., Information about our product'}
                                                    value={formData.subject}
                                                    onChange={handleChange}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Message */}
                                    <Row className="mb-3">
                                        <Col>
                                            <Form.Group>
                                                <Form.Label className="fw-bold">Message</Form.Label>
                                                <JoditEditor
                                                    ref={editorRef}
                                                    value={formData.message || ''}
                                                    config={editorConfig}
                                                    tabIndex={1}
                                                    onBlur={newContent => setFormData(prev => ({ ...prev, message: newContent }))}
                                                    onChange={newContent => setFormData(prev => ({ ...prev, message: newContent }))}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    {/* Attachments */}
                                    <Row className="mb-2">
                                        <Col>
                                            <Form.Group>
                                                <Form.Label className="fw-bold">Attachments</Form.Label>
                                                <Form.Control type="file" name="files" ref={fileRef} multiple onChange={handleFilesChange} />
                                                {selectedFiles.length > 0 && (
                                                    <div className="mt-2">
                                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.9rem' }}>
                                                            {selectedFiles.map((f, i) => (
                                                                <li key={i}>{f.name} ({Math.round(f.size / 1024)} KB)</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {modifymode && formData.attachments && formData.attachments.length > 0 && (
                                                    <div className="mt-2">
                                                        <strong style={{ fontSize: '0.9rem' }}>Existing Files:</strong>
                                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.9rem' }}>
                                                            {(Array.isArray(formData.attachments)
                                                                ? formData.attachments
                                                                : (() => { try { return JSON.parse(formData.attachments); } catch { return []; } })()
                                                            ).map((f, i) => {
                                                                const name = typeof f === 'string' ? f : (f.filename || '');
                                                                const parts = name.split(/[/\\]/);
                                                                return <li key={i}>{parts[parts.length - 1]}</li>;
                                                            })}
                                                        </ul>
                                                    </div>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>

                        {modifymode && <Form.Control type="hidden" name="id" value={formData.id || ''} />}

                        <div className="d-flex justify-content-end">
                            <Button className="mx-1" variant="secondary" onClick={closeForm} disabled={saving}>Cancel</Button>
                            <Button type="submit" className="mx-1" variant="primary" disabled={saving}>
                                {saving ? (modifymode ? 'Updating...' : 'Saving...') : (modifymode ? 'Update' : 'Save')}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </React.Fragment>
    );
}

export default VCTemplates;